import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Calculator, TrendingUp, Info, Target, AlertTriangle } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const STORAGE_KEY = 'savings_goal_data';

export const SavingsGoalCalculator = () => {
  const [targetAmount, setTargetAmount] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).targetAmount : 1000000;
  });
  
  const [timeHorizon, setTimeHorizon] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).timeHorizon : 60;
  });
  
  const [currentSavings, setCurrentSavings] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).currentSavings : 100000;
  });
  
  const [monthlyContribution, setMonthlyContribution] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).monthlyContribution : 10000;
  });
  
  const [expectedReturn, setExpectedReturn] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).expectedReturn : 8;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      targetAmount,
      timeHorizon,
      currentSavings,
      monthlyContribution,
      expectedReturn
    }));
  }, [targetAmount, timeHorizon, currentSavings, monthlyContribution, expectedReturn]);

  const calculate = () => {
    const target = parseFloat(targetAmount) || 0;
    const months = parseFloat(timeHorizon) || 0;
    const current = parseFloat(currentSavings) || 0;
    const contribution = parseFloat(monthlyContribution) || 0;
    const rate = (parseFloat(expectedReturn) || 0) / 100 / 12;

    // Future value of current savings
    const fvCurrent = current * Math.pow(1 + rate, months);
    
    // Future value of monthly contributions (annuity)
    const fvContributions = contribution * ((Math.pow(1 + rate, months) - 1) / rate);
    
    const totalAccumulated = fvCurrent + fvContributions;
    const totalInvested = current + (contribution * months);
    const totalGains = totalAccumulated - totalInvested;
    const shortfall = target - totalAccumulated;
    const isFeasible = totalAccumulated >= target;
    
    // Calculate required monthly savings if shortfall exists
    let requiredMonthly = 0;
    if (!isFeasible && months > 0) {
      const remainingGoal = target - fvCurrent;
      if (remainingGoal > 0) {
        requiredMonthly = (remainingGoal * rate) / (Math.pow(1 + rate, months) - 1);
      }
    }

    return {
      totalAccumulated,
      totalInvested,
      totalGains,
      shortfall,
      isFeasible,
      requiredMonthly,
      gainsPercent: totalInvested > 0 ? (totalGains / totalInvested) * 100 : 0
    };
  };

  const generateAccumulationData = () => {
    const months = parseFloat(timeHorizon) || 0;
    const current = parseFloat(currentSavings) || 0;
    const contribution = parseFloat(monthlyContribution) || 0;
    const rate = (parseFloat(expectedReturn) || 0) / 100 / 12;

    const data = [];
    let accumulated = current;
    let totalContributions = current;

    for (let month = 0; month <= months; month++) {
      if (month > 0) {
        accumulated = accumulated * (1 + rate) + contribution;
        totalContributions += contribution;
      }
      
      const gains = accumulated - totalContributions;
      
      data.push({
        month,
        accumulated: Math.round(accumulated),
        contributions: Math.round(totalContributions),
        gains: Math.round(gains)
      });
    }

    return data;
  };

  const generateGoalGapData = () => {
    const results = calculate();
    const target = parseFloat(targetAmount) || 0;
    
    return [
      { category: 'Target Amount', amount: target },
      { category: 'Projected Savings', amount: results.totalAccumulated },
      { category: 'Gap', amount: results.isFeasible ? 0 : Math.abs(results.shortfall) }
    ];
  };

  const generateReturnSensitivityData = () => {
    const months = parseFloat(timeHorizon) || 0;
    const current = parseFloat(currentSavings) || 0;
    const contribution = parseFloat(monthlyContribution) || 0;
    
    const rates = [4, 6, 8, 10, 12];
    
    return rates.map(returnRate => {
      const rate = returnRate / 100 / 12;
      const fvCurrent = current * Math.pow(1 + rate, months);
      const fvContributions = contribution * ((Math.pow(1 + rate, months) - 1) / rate);
      const total = fvCurrent + fvContributions;
      
      return {
        rate: `${returnRate}%`,
        amount: Math.round(total)
      };
    });
  };

  const results = calculate();
  const accumulationData = generateAccumulationData();
  const goalGapData = generateGoalGapData();
  const sensitivityData = generateReturnSensitivityData();

  const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-display text-3xl font-semibold text-foreground">
              Savings Goal Calculator
            </h1>
          </div>
          <p className="text-muted-foreground">Plan your savings journey and achieve your financial goals</p>
        </div>

        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Savings Goal Planning?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Savings goal planning helps you understand how much you need to save regularly to achieve your financial targets. 
                  By factoring in time, current savings, and expected returns, you can create a realistic roadmap to your goals.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your savings details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="targetAmount" className="text-sm font-medium">
                      Target Amount ($)
                    </Label>
                    <Input
                      id="targetAmount"
                      type="number"
                      value={targetAmount}
                      onChange={(e) => setTargetAmount(e.target.value)}
                      placeholder="1000000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Your financial goal amount</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timeHorizon" className="text-sm font-medium">
                      Time Horizon (Months)
                    </Label>
                    <Input
                      id="timeHorizon"
                      type="number"
                      value={timeHorizon}
                      onChange={(e) => setTimeHorizon(e.target.value)}
                      placeholder="60"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">{(parseFloat(timeHorizon) / 12).toFixed(1)} years</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="currentSavings" className="text-sm font-medium">
                      Current Savings ($)
                    </Label>
                    <Input
                      id="currentSavings"
                      type="number"
                      value={currentSavings}
                      onChange={(e) => setCurrentSavings(e.target.value)}
                      placeholder="100000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="monthlyContribution" className="text-sm font-medium">
                      Monthly Contribution ($)
                    </Label>
                    <Input
                      id="monthlyContribution"
                      type="number"
                      value={monthlyContribution}
                      onChange={(e) => setMonthlyContribution(e.target.value)}
                      placeholder="10000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="expectedReturn" className="text-sm font-medium">
                      Expected Return Rate (% p.a.)
                    </Label>
                    <Input
                      id="expectedReturn"
                      type="number"
                      value={expectedReturn}
                      onChange={(e) => setExpectedReturn(e.target.value)}
                      placeholder="8"
                      step="0.1"
                      className="text-base"
                    />
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setTargetAmount(targetAmount)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Savings Goal Analysis</CardTitle>
                    <CardDescription>Your savings projection</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className={`p-6 rounded-xl bg-gradient-to-br ${results.isFeasible ? 'from-secondary-light to-card border-secondary/20' : 'from-destructive/10 to-card border-destructive/20'} border`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-lg ${results.isFeasible ? 'bg-secondary' : 'bg-destructive'} flex items-center justify-center`}>
                            {results.isFeasible ? <Target className="w-5 h-5 text-white" /> : <AlertTriangle className="w-5 h-5 text-white" />}
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Goal Status</p>
                        </div>
                        <p className={`text-3xl font-display font-bold ${results.isFeasible ? 'text-secondary' : 'text-destructive'}`}>
                          {results.isFeasible ? 'Achievable' : 'Shortfall'}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {results.isFeasible ? 'On track to meet goal' : `Need ${formatCurrency(Math.abs(results.shortfall))} more`}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Accumulated</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(results.totalAccumulated)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Investment</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {formatCurrency(results.totalInvested)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Gains</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.totalGains)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{results.gainsPercent.toFixed(1)}% returns</p>
                      </div>
                    </div>

                    {!results.isFeasible && (
                      <>
                        <Separator className="my-6" />
                        <div className="p-6 rounded-xl bg-destructive/10 border border-destructive/20">
                          <h4 className="font-semibold text-destructive mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5" />
                            Action Required
                          </h4>
                          <p className="text-sm text-muted-foreground mb-2">
                            To achieve your goal of {formatCurrency(parseFloat(targetAmount))}, you need to:
                          </p>
                          <p className="text-2xl font-display font-bold text-destructive mb-2">
                            Save {formatCurrency(results.requiredMonthly)} per month
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Increase from {formatCurrency(parseFloat(monthlyContribution))} by {formatCurrency(results.requiredMonthly - parseFloat(monthlyContribution))}
                          </p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Savings Accumulation Over Time</CardTitle>
                <CardDescription className="text-base">
                  Track your savings growth journey
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={accumulationData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="month" 
                        label={{ value: 'Months', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 100000).toFixed(0)}L`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="contributions" 
                        stackId="1"
                        stroke="hsl(var(--primary))" 
                        fill="hsl(var(--primary))"
                        fillOpacity={0.6}
                        name="Contributions"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="gains" 
                        stackId="1"
                        stroke="hsl(var(--secondary))" 
                        fill="hsl(var(--secondary))"
                        fillOpacity={0.6}
                        name="Investment Gains"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Goal Gap Analysis</CardTitle>
                  <CardDescription>
                    Are you on track?
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={goalGapData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="category" 
                          stroke="hsl(var(--muted-foreground))"
                          angle={-15}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 100000).toFixed(0)}L`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="amount" fill="hsl(var(--primary))" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Return Rate Sensitivity</CardTitle>
                  <CardDescription>
                    Impact of different return rates
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={sensitivityData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="rate" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 100000).toFixed(0)}L`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="amount" 
                          stroke="hsl(var(--secondary))" 
                          strokeWidth={3}
                          name="Final Amount"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your Savings Goal</CardTitle>
                <CardDescription className="text-base">
                  Learn how to achieve your financial targets faster
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Power of Compound Interest
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your total investment is <strong className="text-primary">{formatCurrency(results.totalInvested)}</strong>, 
                    but with {expectedReturn}% annual returns, you'll accumulate <strong className="text-primary">{formatCurrency(results.totalAccumulated)}</strong>. 
                    That's <strong>{formatCurrency(results.totalGains)}</strong> in gains!
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm">
                    <p className="font-mono mb-2">The magic of compound interest:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Your money earns returns</li>
                      <li>Those returns earn more returns</li>
                      <li>Time amplifies the effect exponentially</li>
                    </ul>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Goal Feasibility Assessment
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Your goal of {formatCurrency(parseFloat(targetAmount))} in {timeHorizon} months ({(parseFloat(timeHorizon) / 12).toFixed(1)} years) is currently 
                    <strong className={results.isFeasible ? 'text-secondary' : 'text-destructive'}> {results.isFeasible ? 'ACHIEVABLE' : 'UNDERFUNDED'}</strong>.
                  </p>
                  
                  {!results.isFeasible && (
                    <div className="space-y-4 text-base">
                      <div className="p-4 rounded-lg bg-destructive/10 border-l-4 border-destructive">
                        <h4 className="font-semibold text-foreground mb-2">⚠️ Underfunding Warning</h4>
                        <p className="text-muted-foreground">
                          At your current savings rate, you'll fall short by {formatCurrency(Math.abs(results.shortfall))}. 
                          Consider increasing monthly contributions or extending the time horizon.
                        </p>
                      </div>
                    </div>
                  )}

                  {results.isFeasible && (
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ On Track</h4>
                      <p className="text-muted-foreground">
                        Great job! You're projected to exceed your goal by {formatCurrency(results.totalAccumulated - parseFloat(targetAmount))} 
                        if you maintain this savings rate.
                      </p>
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Goal Acceleration Strategies
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">🚀 Increase Monthly Savings</h4>
                      <p className="text-muted-foreground">
                        Even a small increase in monthly contributions can significantly accelerate your goal. Try the 1% rule: 
                        increase savings by 1% of your income every year.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">🚀 Seek Better Returns</h4>
                      <p className="text-muted-foreground">
                        A 2% increase in returns can dramatically impact your final amount. Consider diversifying into equity 
                        mutual funds or balanced portfolios for better long-term returns.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">🚀 Invest Windfalls</h4>
                      <p className="text-muted-foreground">
                        Bonuses, tax refunds, or unexpected income should go directly into your savings goal. These lump-sum 
                        additions benefit from maximum compounding time.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Important Considerations
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Inflation Impact:</strong> Remember that inflation erodes purchasing power. 
                        For a goal 5+ years away, factor in 5-6% annual inflation when setting your target.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Return Assumptions:</strong> Markets fluctuate. The expected return is 
                        an average - actual returns will vary year to year. Conservative estimates are safer for critical goals.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Emergency Buffer:</strong> Don't lock all savings into long-term goals. 
                        Maintain 3-6 months of expenses in liquid emergency funds.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Review Regularly:</strong> Life changes - income, expenses, goals. 
                        Review and adjust your savings plan at least annually.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Smart Savings Tips
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">💡 Automate Savings</h4>
                      <p className="text-muted-foreground">
                        Set up automatic transfers on salary day. "Pay yourself first" before you have a chance to spend.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">💡 Split Large Goals</h4>
                      <p className="text-muted-foreground">
                        Break big goals into milestones. Celebrating smaller wins keeps you motivated for the long journey.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">💡 Track Progress Monthly</h4>
                      <p className="text-muted-foreground">
                        Review your savings monthly. Seeing progress motivates continued discipline and highlights any deviations early.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SavingsGoalCalculator;
